import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Button, Card, EmptyState, Field, Modal, Spinner } from '../../components/ui';
import { useToast } from '../../components/Toast';

const EMPTY_PROFILE = {
  firstName: '', lastName: '', phone: '', familyName: '',
  secondaryContactName: '', secondaryContactPhone: '',
  addressLine1: '', addressLine2: '', suburb: '', city: '', region: '', postcode: '', country: '',
  emergencyContactName: '', emergencyContactPhone: '',
  pets: '', householdNotes: '', nannyRequirements: '',
};

const EMPTY_CHILD = { firstName: '', dateOfBirth: '', age: '', allergies: '', notes: '' };

export default function FamilyProfile() {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_PROFILE);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [childModal, setChildModal] = useState(null);

  function hydrate({ family, children: kids }) {
    const next = { ...EMPTY_PROFILE };
    Object.keys(EMPTY_PROFILE).forEach((key) => {
      if (family.profile[key] !== undefined) next[key] = family.profile[key];
    });
    setForm({ ...next, firstName: family.firstName, lastName: family.lastName, phone: family.phone });
    setChildren(kids);
  }

  useEffect(() => {
    api
      .get('/api/family/profile')
      .then(hydrate)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [toast]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setFieldErrors({});
    try {
      hydrate(await api.put('/api/family/profile', form));
      toast.success(t('familyProfile.profileSaved'));
    } catch (err) {
      toast.error(err.message);
      setFieldErrors(err.fieldErrors || {});
    } finally {
      setSaving(false);
    }
  }

  async function saveChild(child) {
    const payload = {
      firstName: child.firstName, dateOfBirth: child.dateOfBirth,
      age: child.age, allergies: child.allergies, notes: child.notes,
    };
    const result = child.id
      ? await api.put(`/api/family/children/${child.id}`, payload)
      : await api.post('/api/family/children', payload);
    setChildren(result.children);
    setChildModal(null);
    toast.success(t('familyProfile.childSaved'));
  }

  async function removeChild(id) {
    if (!window.confirm(t('familyProfile.confirmRemoveChild'))) return;
    const { children: next } = await api.del(`/api/family/children/${id}`);
    setChildren(next);
  }

  if (loading) return <Spinner />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('familyProfile.pageTitle')}</h1>
          <p className="muted">{t('familyProfile.subtitle')}</p>
        </div>
      </div>


      <form onSubmit={save}>
        <Card title={t('familyProfile.contactDetails')}>
          <div className="grid-2">
            <Field label={t('familyProfile.firstName')} required error={fieldErrors.firstName}>
              <input value={form.firstName} onChange={set('firstName')} required />
            </Field>
            <Field label={t('familyProfile.lastName')} required error={fieldErrors.lastName}>
              <input value={form.lastName} onChange={set('lastName')} required />
            </Field>
            <Field label={t('familyProfile.phone')}><input value={form.phone} onChange={set('phone')} /></Field>
            <Field label={t('familyProfile.familyName')} hint={t('familyProfile.familyNameHint')}>
              <input value={form.familyName} onChange={set('familyName')} />
            </Field>
            <Field label={t('familyProfile.secondContactName')}>
              <input value={form.secondaryContactName} onChange={set('secondaryContactName')} />
            </Field>
            <Field label={t('familyProfile.secondContactPhone')}>
              <input value={form.secondaryContactPhone} onChange={set('secondaryContactPhone')} />
            </Field>
          </div>
        </Card>

        <Card title={t('familyProfile.address')}>
          <Field label={t('familyProfile.streetAddress')}><input value={form.addressLine1} onChange={set('addressLine1')} /></Field>
          <Field label={t('familyProfile.aptSuite')}>
            <input value={form.addressLine2} onChange={set('addressLine2')} />
          </Field>
          <div className="grid-2">
            <Field label={t('familyProfile.city')}><input value={form.city} onChange={set('city')} /></Field>
            <Field label={t('familyProfile.stateProvince')}><input value={form.region} onChange={set('region')} /></Field>
            <Field label={t('familyProfile.postalCode')}><input value={form.postcode} onChange={set('postcode')} /></Field>
            <Field label={t('familyProfile.country')}><input value={form.country} onChange={set('country')} /></Field>
          </div>
        </Card>

        <Card title={t('familyProfile.householdRequirements')}>
          <Field label={t('familyProfile.lookingFor')} hint={t('familyProfile.lookingForHint')}>
            <textarea rows={4} value={form.nannyRequirements} onChange={set('nannyRequirements')} />
          </Field>
          <Field label={t('familyProfile.householdNotes')} hint={t('familyProfile.householdNotesHint')}>
            <textarea rows={3} value={form.householdNotes} onChange={set('householdNotes')} />
          </Field>
          <Field label={t('familyProfile.pets')}><input value={form.pets} onChange={set('pets')} /></Field>
          <div className="grid-2">
            <Field label={t('familyProfile.emergencyContactName')}>
              <input value={form.emergencyContactName} onChange={set('emergencyContactName')} />
            </Field>
            <Field label={t('familyProfile.emergencyContactPhone')}>
              <input value={form.emergencyContactPhone} onChange={set('emergencyContactPhone')} />
            </Field>
          </div>
        </Card>

        <div className="form-footer">
          <Button type="submit" loading={saving}>{t('familyProfile.saveProfile')}</Button>
        </div>
      </form>

      <Card
        title={t('familyProfile.children')}
        actions={
          <Button type="button" variant="secondary" onClick={() => setChildModal({ ...EMPTY_CHILD })}>
            {t('familyProfile.addChild')}
          </Button>
        }
      >
        {children.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>{t('familyProfile.name')}</th><th>{t('familyProfile.age')}</th><th>{t('familyProfile.allergies')}</th><th>{t('familyProfile.notes')}</th><th /></tr>
              </thead>
              <tbody>
                {children.map((child) => (
                  <tr key={child.id}>
                    <td><strong>{child.firstName}</strong></td>
                    <td>{child.age || child.dateOfBirth || '—'}</td>
                    <td>{child.allergies || '—'}</td>
                    <td>{child.notes || '—'}</td>
                    <td className="row-actions">
                      <button type="button" className="link" onClick={() => setChildModal(child)}>{t('familyProfile.edit')}</button>
                      <button type="button" className="link danger" onClick={() => removeChild(child.id)}>{t('familyProfile.remove')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title={t('familyProfile.noChildrenYet')}>{t('familyProfile.noChildrenBody')}</EmptyState>
        )}
      </Card>

      {childModal && (
        <ChildModal
          child={childModal}
          onClose={() => setChildModal(null)}
          onSave={saveChild}
        />
      )}
    </>
  );
}

function ChildModal({ child, onClose, onSave }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState(child);
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      await onSave(form);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={child.id ? t('familyProfile.editChild') : t('familyProfile.addChildTitle')} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label={t('familyProfile.name')} required>
          <input value={form.firstName} onChange={set('firstName')} required />
        </Field>
        <div className="grid-2">
          <Field label={t('familyProfile.dateOfBirth')}>
            <input type="date" value={form.dateOfBirth || ''} onChange={set('dateOfBirth')} />
          </Field>
          <Field label={t('familyProfile.age')} hint={t('familyProfile.ageHint')}>
            <input value={form.age} onChange={set('age')} />
          </Field>
        </div>
        <Field label={t('familyProfile.allergiesOrMedical')}>
          <textarea rows={2} value={form.allergies} onChange={set('allergies')} />
        </Field>
        <Field label={t('familyProfile.notes')} hint={t('familyProfile.childNotesHint')}>
          <textarea rows={3} value={form.notes} onChange={set('notes')} />
        </Field>
        <div className="form-footer">
          <Button type="button" variant="ghost" onClick={onClose}>{t('familyProfile.cancel')}</Button>
          <Button type="submit" loading={busy}>{t('familyProfile.save')}</Button>
        </div>
      </form>
    </Modal>
  );
}
